import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    // Hover preview. The variant picker is a *sibling* of this gallery rather than
    // an ancestor, so the declarative `on:pointerenter` bindings that product cards
    // use cannot reach us. Delegate from the section both elements share instead.
    target?.addEventListener('pointerover', this.#handlePointerOver, { signal });
    target?.addEventListener('pointerout', this.#handlePointerOut, { signal });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** Media id committed by the last variant selection; restores after a hover preview. */
  #selectedMediaId = null;

  /** Guards against a slow response for a superseded selection. */
  #requestId = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Finds the nearest element carrying a media id, if any.
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #optionFrom(target) {
    if (!(target instanceof Element)) return null;
    const el = target.closest('[data-option-media-id]');
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * Shows a colour's image while the pointer rests on its swatch.
   * @param {PointerEvent} event
   */
  #handlePointerOver = (event) => {
    // Touch taps also emit pointerover; previewing there would fight the click
    // that follows it, so restrict the preview to real pointers.
    if (event.pointerType !== 'mouse') return;

    const mediaId = this.#optionFrom(event.target)?.dataset.optionMediaId;
    if (!mediaId) return;

    this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
  };

  /**
   * Restores the committed selection when the pointer leaves a swatch.
   * @param {PointerEvent} event
   */
  #handlePointerOut = (event) => {
    if (event.pointerType !== 'mouse') return;
    if (!this.#optionFrom(event.target)) return;
    // Ignore movement between children of the same control.
    if (this.#optionFrom(event.relatedTarget)) return;

    const mediaId = this.#selectedMediaId ?? this.#checkedMediaId();
    if (mediaId) this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
  };

  /**
   * The media id of the currently checked option, used when nothing has been
   * committed yet this page view.
   * @returns {string | null}
   */
  #checkedMediaId() {
    const section = this.closest('.shopify-section, dialog');
    const checked = section?.querySelector('input[data-option-media-id]:checked');
    return checked instanceof HTMLElement ? (checked.dataset.optionMediaId ?? null) : null;
  }

  /**
   * Maps an option value id to the media it should display.
   * @param {string | number | undefined} optionValueId
   * @returns {string | null}
   */
  #mediaIdForOptionValue(optionValueId) {
    if (optionValueId == null) return null;

    const section = this.closest('.shopify-section, dialog');
    const selector = `[data-option-value-id="${CSS.escape(String(optionValueId))}"][data-option-media-id]`;
    const el = section?.querySelector(selector);
    return el instanceof HTMLElement ? (el.dataset.optionMediaId ?? null) : null;
  }

  /**
   * The slide ids currently rendered under a gallery root.
   * @param {ParentNode} root
   * @returns {(string | null)[]}
   */
  #slideIds(root) {
    return [...root.querySelectorAll('[ref="slides[]"]')].map((slide) => slide.getAttribute('slide-id'));
  }

  /**
   * Whether an incoming gallery holds the same media, in any order.
   * @param {ParentNode} next
   * @returns {boolean}
   */
  #sameMediaSet(next) {
    const current = new Set(this.#slideIds(this));
    const incoming = new Set(this.#slideIds(next));

    if (!current.size || current.size !== incoming.size) return false;
    return [...current].every((id) => id != null && incoming.has(id));
  }

  /**
   * Handles a product select event by showing the variant's media.
   *
   * The event is dispatched synchronously with the click, so the switch below
   * lands on the same tick rather than after the network round trip.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const mediaId = this.#mediaIdForOptionValue(event.detail?.optionValueId);
    const requestId = ++this.#requestId;

    this.#selectedMediaId = mediaId;
    if (mediaId) this.slideshow?.select({ id: mediaId }, undefined, { animate: false });

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || requestId !== this.#requestId) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // The server hoists the selected variant's media to index 0, so a same-set
        // response differs only in order. Replacing on that would reset the
        // slideshow and reshuffle the thumbnail strip under the user's finger,
        // visibly undoing the switch above. Keep our DOM and re-assert the choice.
        // A genuinely different set — a combined listing, or `hide_variants` — still
        // falls through to the replacement, preserving the previous behaviour.
        if (this.#sameMediaSet(newMediaGallery)) {
          if (mediaId) this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
